"use client"

import { Text } from "@/elements"
import { useSession } from "@/hooks/useSession"
import { NovaLogo, NovaWordmark } from "@/svg"
import { AppleLogo } from "@/svg/AppleLogo"
import type { NovaSession } from "@/types"
import { Bars2Icon, XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import Link from "next/link"
import { useEffect, useState } from "react"
import { ProfileLinkButton } from "./ProfileLinkButton"
import { SignUpButton } from "./SignUpButton"

export const getProfileLinkDetails = (serverSession: NovaSession | null | undefined) => {
  if (!serverSession) return null
  if (
    serverSession.user.creative.id &&
    (serverSession.account === "creative" || (serverSession.account === "client" && !serverSession.user.client.id))
  ) {
    return {
      href: serverSession.user.creative.handle,
      switchAccount: serverSession.account === "creative" ? false : true,
    }
  }

  if (
    serverSession.user.client.id &&
    (serverSession.account === "client" || (serverSession.account === "creative" && !serverSession.user.creative.id))
  ) {
    return {
      href: `/clients/${serverSession.user.client.handle}`,
      switchAccount: serverSession.account === "client" ? false : true,
    }
  }

  return null
}
export const FloatingNav = ({ serverSession }: { serverSession?: NovaSession | null }) => {
  const hasProfile = serverSession?.user.creative.id || serverSession?.user.client.id
  const profileDetails = getProfileLinkDetails(serverSession)
  const [isOpenMobile, setIsOpenMobile] = useState(false)

  const { update } = useSession()
  const handleProfileSwitch = async () => {
    if (profileDetails?.switchAccount) {
      const newAccount = profileDetails.switchAccount
        ? serverSession?.account === "creative"
          ? "client"
          : "creative"
        : serverSession?.account
      await update({
        account: newAccount,
      })
    }
  }

  useEffect(() => {
    if (isOpenMobile) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
  }, [isOpenMobile])

  return (
    <>
      <div
        style={{
          backdropFilter: "blur(60px)",
          WebkitBackdropFilter: "blur(60px)",
        }}
        className="pointer-events-auto relative mx-6 flex h-12 w-full max-w-[680px] items-center justify-between overflow-hidden rounded-full bg-bg-7/[0.08] px-6 py-3 transition-height sm:h-16 sm:p-2 sm:pl-7 dark:bg-bg-1/[0.08]">
        <Link href="/">
          <h1>
            <NovaWordmark
              className="hidden fill-marketing-primary-light sm:block dark:fill-marketing-primary-dark"
              height={18}
              width={72}
            />
          </h1>
          <h1>
            <NovaLogo
              className="fill-marketing-primary-light sm:hidden dark:fill-marketing-primary-dark"
              width={17}
              height={18}
            />
          </h1>
        </Link>
        {/* desktop */}
        <div className="hidden flex-row items-center gap-x-6 sm:flex">
          <Link
            href="https://www.novastudios.la"
            target="_blank"
            className="hover group flex h-12 flex-col overflow-hidden">
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-fg-primary group-hover:translate-y-[-100%] group-hover:opacity-0">
              Studio
            </Text>
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-fg-primary opacity-0 group-hover:translate-y-[-100%] group-hover:opacity-100">
              Studio
            </Text>
          </Link>
          <Link href="/enterprise" className="hover group flex h-12 flex-col overflow-hidden">
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light/50 group-hover:translate-y-[-100%] group-hover:opacity-0 dark:text-marketing-primary-dark/50">
              Enterprise
            </Text>
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light opacity-0 group-hover:translate-y-[-100%] group-hover:opacity-100 dark:text-marketing-primary-dark">
              Enterprise
            </Text>
          </Link>
          <Link href="/blog" className="hover group flex h-12 flex-col overflow-hidden">
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light/50 group-hover:translate-y-[-100%] group-hover:opacity-0 dark:text-marketing-primary-dark/50">
              Blog
            </Text>
            <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light opacity-0 group-hover:translate-y-[-100%] group-hover:opacity-100 dark:text-marketing-primary-dark">
              Blog
            </Text>
          </Link>

          {hasProfile ? (
            <ProfileLinkButton serverSession={serverSession} />
          ) : (
            <>
              <Link className="hover group flex h-12 flex-col overflow-hidden" href="/login">
                <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light/50 group-hover:translate-y-[-100%] group-hover:opacity-0 dark:text-marketing-primary-dark/50">
                  Login
                </Text>
                <Text className="marketing-transition-button-text h-12 shrink-0 content-center text-marketing-primary-light opacity-0 group-hover:translate-y-[-100%] group-hover:opacity-100 dark:text-marketing-primary-dark">
                  Login
                </Text>
              </Link>
              <SignUpButton>Sign up</SignUpButton>
            </>
          )}
        </div>
        {/* mobile */}
        <div className="z-10 flex items-center gap-x-6 sm:hidden">
          {hasProfile ? (
            <Link
              onClick={handleProfileSwitch}
              href={profileDetails?.href || "/sign-up"}
              className="pointer-events-auto">
              <Text className="text-marketing-primary-light dark:text-marketing-primary-dark">Go to profile</Text>
            </Link>
          ) : (
            <Link href="/sign-up" className="pointer-events-auto">
              <Text className="text-marketing-primary-light dark:text-marketing-primary-dark">Sign up</Text>
            </Link>
          )}
          <Bars2Icon
            onClick={() => setIsOpenMobile(true)}
            className="size-6 stroke-marketing-primary-light dark:stroke-marketing-primary-dark"
          />
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="152"
          height="152"
          viewBox="0 0 152 152"
          fill="none"
          className="absolute bottom-[-75px] right-5 sm:hidden">
          <g filter="url(#filter0_f_3573_4167)">
            <circle cx="75.7983" cy="75.7983" r="25.7983" fill="#9747FF" fillOpacity="0.2" />
          </g>
          <g filter="url(#filter1_f_3573_4167)">
            <circle cx="75.7983" cy="75.7983" r="15.2983" fill="#F778C9" fillOpacity="0.6" />
          </g>
          <defs>
            <filter
              id="filter0_f_3573_4167"
              x="0"
              y="0"
              width="151.597"
              height="151.597"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="25" result="effect1_foregroundBlur_3573_4167" />
            </filter>
            <filter
              id="filter1_f_3573_4167"
              x="10.5"
              y="10.5"
              width="130.597"
              height="130.597"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="25" result="effect1_foregroundBlur_3573_4167" />
            </filter>
          </defs>
        </svg>
      </div>
      <div
        className={clsx(
          "absolute top-[-36px] z-[10000] flex h-[100dvh] w-full flex-col justify-end bg-marketing-background-1-light transition-all duration-300 ease-in-out dark:bg-marketing-background-1-dark",
          isOpenMobile ? "pointer-events-auto translate-y-0" : "pointer-events-none -translate-y-full"
        )}>
        <XMarkIcon
          onClick={() => setIsOpenMobile(false)}
          className="absolute right-12 top-12 size-6 stroke-marketing-primary-light dark:stroke-marketing-primary-dark"
        />
        <div className="flex flex-col gap-y-16 px-6">
          <div className="flex flex-col gap-y-5">
            <Link href="/">
              <Text variant="h2" className="text-marketing-primary-light dark:text-marketing-primary-dark">
                Home
              </Text>
            </Link>
            <Link href="https://www.novastudios.la" target="_blank">
              <Text variant="h2" className="text-marketing-primary-light dark:text-marketing-primary-dark">
                Studio
              </Text>
            </Link>
            <Link href="/enterprise">
              <Text variant="h2" className="text-marketing-primary-light dark:text-marketing-primary-dark">
                Enterprise
              </Text>
            </Link>
            <Link href="/blog">
              <Text variant="h2" className="text-marketing-primary-light dark:text-marketing-primary-dark">
                Blog
              </Text>
            </Link>
            <Link href="/sign-up">
              <Text variant="h2" className="text-marketing-brand-1">
                Sign up
              </Text>
            </Link>
            <Link href="/login">
              <Text variant="h2" className="text-marketing-secondary">
                Login
              </Text>
            </Link>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex flex-col gap-y-2">
              <Link href="https://www.itsnova.com">
                <Text variant="h6" className="text-marketing-secondary">
                  Instagram
                </Text>
              </Link>
              <Link href="mailto:contact@itsnova.com">
                <Text variant="h6" className="text-marketing-secondary">
                  Email
                </Text>
              </Link>
              <Link href="/faqs">
                <Text variant="h6" className="text-marketing-secondary">
                  FAQ
                </Text>
              </Link>
            </div>
            <Link
              href="https://go.itsnova.com/appstore"
              target="_blank"
              className="flex h-fit flex-row items-center gap-x-2">
              <AppleLogo className="size-[22px] fill-marketing-primary-light dark:fill-marketing-primary-dark" />
              <Text variant="h6" className="text-marketing-primary-light dark:text-marketing-primary-dark">
                Get the app
              </Text>
            </Link>
          </div>
          <NovaWordmark className="h-auto w-full fill-marketing-background-2-light" />
        </div>
      </div>
    </>
  )
}
